<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';
import WhatsAppCampaignDetails from './WhatsAppCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isWhatsAppType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  createdAt: {
    type: Number,
    default: 0,
  },
  updatedAt: {
    type: Number,
    default: 0,
  },
  templateParams: {
    type: Object,
    default: null,
  },
  campaignMetrics: {
    type: Object,
    default: null,
  },
  suppressionCount: {
    type: Number,
    default: 0,
  },
  variantLabel: {
    type: String,
    default: '',
  },
  lifecyclePermissions: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['edit', 'delete', 'duplicate', 'suppressions']);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value,
  'text-n-slate-12': !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  const statusLabels = {
    completed: t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED'),
    draft: t('CAMPAIGN.WHATSAPP.CARD.STATUS.DRAFT', 'Draft'),
    scheduled: t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED'),
    sending: t('CAMPAIGN.WHATSAPP.CARD.STATUS.SENDING', 'Sending'),
    cancelled: t('CAMPAIGN.WHATSAPP.CARD.STATUS.CANCELLED', 'Cancelled'),
    failed: t('CAMPAIGN.WHATSAPP.CARD.STATUS.FAILED', 'Failed'),
  };

  return statusLabels[props.status] || t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});

const isCompleted = computed(() => props.status === STATUS_COMPLETED);
const isEditableWhatsApp = computed(
  () => props.isWhatsAppType && props.lifecyclePermissions.editable
);

const templateName = computed(() => props.templateParams?.name || '');
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          v-if="variantLabel"
          class="text-xs font-semibold inline-flex items-center h-6 px-2 py-0.5 rounded-md"
          :class="
            variantLabel === 'A'
              ? 'bg-n-blue-3 text-n-blue-11'
              : 'bg-n-violet-3 text-n-violet-11'
          "
        >
          {{ variantLabel }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <WhatsAppCampaignDetails
          v-else-if="isWhatsAppType"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
          :created-at="createdAt"
          :updated-at="updatedAt"
          :template-name="templateName"
          :metrics="campaignMetrics"
          :suppression-count="suppressionCount"
          :is-completed="isCompleted"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
    </div>
    <div class="flex items-center justify-end gap-2">
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click="emit('edit')"
      />
      <Button
        v-if="isWhatsAppType && (isCompleted || isEditableWhatsApp)"
        variant="faded"
        size="sm"
        color="slate"
        :icon="
          isCompleted ? 'i-lucide-bar-chart-2' : 'i-lucide-sliders-vertical'
        "
        @click="emit('edit')"
      />
      <Button
        v-if="isWhatsAppType && status !== 'sending'"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-copy"
        @click="emit('duplicate')"
      />
      <Button
        v-if="isWhatsAppType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-shield-ban"
        @click="emit('suppressions')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
